// Phase of an event against the clock (web spec SW-EV-01, SW-EV-02): shown as a badge and used to sort the list.
export type EventPhase = "UPCOMING" | "ONGOING" | "ENDED";

export interface PhasedEvent {
  startAt: string;
  endAt: string;
  status?: string;
  name?: string;
}

export function phase(e: Pick<PhasedEvent, "startAt" | "endAt">, now: Date): EventPhase {
  const ms = now.getTime();
  if (ms >= Date.parse(e.endAt)) return "ENDED";
  if (ms >= Date.parse(e.startAt)) return "ONGOING";
  return "UPCOMING";
}

/**
 * Default order of the event list: ongoing first (ending soonest first), then upcoming by start, then ended
 * (most recent first). Archived events come last, in the same order.
 */
export function compareEvents(a: PhasedEvent, b: PhasedEvent, now: Date): number {
  const archived = (e: PhasedEvent) => (e.status === "ARCHIVED" ? 1 : 0);
  if (archived(a) !== archived(b)) return archived(a) - archived(b);
  const rank: Record<EventPhase, number> = { ONGOING: 0, UPCOMING: 1, ENDED: 2 };
  const pa = phase(a, now), pb = phase(b, now);
  if (pa !== pb) return rank[pa] - rank[pb];
  const byDate = pa === "ONGOING" ? Date.parse(a.endAt) - Date.parse(b.endAt) : pa === "UPCOMING" ? Date.parse(a.startAt) - Date.parse(b.startAt) : Date.parse(b.startAt) - Date.parse(a.startAt);
  // same instant: the name decides, so that the order never depends on the server
  return byDate !== 0 ? byDate : (a.name ?? "").localeCompare(b.name ?? "", "fr");
}

/** Case and accent insensitive match of the search field on the event name. */
export function matchesName(name: string, query: string): boolean {
  const fold = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  const q = fold(query);
  return q.length === 0 || fold(name).includes(q);
}
